import { useState, type KeyboardEvent, type PointerEvent } from "react";

/**
 * Resizing a width from a vertical separator on the right edge of a panel:
 * drag (with a live preview), double-click to reset, arrow keys to step.
 *
 * `value` is the settled width. While a drag is in progress, `shownValue` is
 * the width under the pointer, and `onCommit` fires once, on release, so a
 * caller that persists the width writes it once per drag. The drag captures the
 * pointer, so the moves keep coming to the separator (and keep its cursor)
 * wherever the pointer goes, and nothing is left listening on unmount.
 */
export function useDragResize({
  value,
  min,
  max,
  step,
  reset,
  onCommit,
}: {
  value: number;
  min: number;
  max: number;
  /** One arrow-key press. */
  step: number;
  /** The width a double-click goes back to. */
  reset: number;
  /** Called with a width already clamped to `[min, max]`. */
  onCommit: (value: number) => void;
}) {
  const [drag, setDrag] = useState<{
    startX: number;
    startValue: number;
    draft: number;
  } | null>(null);

  function clamp(next: number): number {
    return Math.min(max, Math.max(min, Math.round(next)));
  }

  function endDrag(): void {
    if (drag === null) return;
    setDrag(null);
    onCommit(drag.draft);
  }

  const shownValue = drag === null ? value : drag.draft;

  return {
    shownValue,
    isDragging: drag !== null,
    separatorProps: {
      role: "separator" as const,
      "aria-orientation": "vertical" as const,
      "aria-valuemin": min,
      "aria-valuemax": max,
      "aria-valuenow": shownValue,
      tabIndex: 0,
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
        if (event.button !== 0) return;
        // Also keeps the drag from selecting the text it passes over.
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setDrag({ startX: event.clientX, startValue: value, draft: value });
      },
      onPointerMove: (event: PointerEvent<HTMLElement>) => {
        if (drag === null) return;
        setDrag({
          ...drag,
          draft: clamp(drag.startValue + event.clientX - drag.startX),
        });
      },
      onPointerUp: endDrag,
      onLostPointerCapture: endDrag,
      onDoubleClick: () => {
        onCommit(reset);
      },
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          onCommit(clamp(value - step));
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          onCommit(clamp(value + step));
        }
      },
    },
  };
}
